import { memo } from "react";
import { Users } from "lucide-react";
import { getProfileImageUrl } from "@/services/api";
import type { ChatRoomWithDetails } from "@/services/chatService";

/**
 * 카카오톡 스타일 채팅방 프로필 아바타.
 * 상대 멤버 수에 따라 1~4분할로 프로필 이미지를 겹쳐 보여준다.
 * (채팅방 목록·공유받기 화면이 같은 모양을 쓰도록 공용 컴포넌트로 분리)
 */
const RoomProfileAvatar = memo(
  ({
    room,
    currentUserId,
    compact = false,
  }: {
    room: ChatRoomWithDetails;
    currentUserId?: string;
    compact?: boolean;
  }) => {
    const otherMembers = room.members?.filter((m) => m.user_id !== currentUserId) || [];
    const memberCount = otherMembers.length;
    const box = compact ? "h-10 w-10" : "h-12 w-12";
    const half = compact ? "h-6 w-6" : "h-7 w-7";
    const third = compact ? "h-5 w-5" : "h-6 w-6";
    const quarter = compact ? "h-[18px] w-[18px]" : "h-[22px] w-[22px]";

    const renderAvatar = (
      member: (typeof otherMembers)[0],
      size: string,
      fontSize: string,
    ) => {
      const userId = member.user_id;
      const hasProfileImage = member.user?.profile_image;
      const name = member.user?.name || "?";

      const initial = (
        <div
          className={`${size} rounded-full bg-primary/20 flex items-center justify-center`}
        >
          <span className={`${fontSize} font-medium text-primary`}>{name.charAt(0)}</span>
        </div>
      );

      if (hasProfileImage && userId) {
        return (
          <>
            <img
              src={getProfileImageUrl(userId)}
              alt={name}
              className={`${size} rounded-full object-cover`}
              onError={(e) => {
                // 이미지 로드 실패 시 이니셜로 대체 (아래 형제 요소를 노출)
                e.currentTarget.style.display = "none";
                e.currentTarget.nextElementSibling?.classList.remove("hidden");
              }}
            />
            <div className="hidden">{initial}</div>
          </>
        );
      }
      return initial;
    };

    if (memberCount === 0) {
      return (
        <div
          className={`${box} flex-shrink-0 rounded-full bg-primary/10 flex items-center justify-center`}
        >
          <Users className="h-5 w-5 text-muted-foreground" />
        </div>
      );
    }

    if (memberCount === 1) {
      return (
        <div className={`${box} flex-shrink-0`}>
          {renderAvatar(otherMembers[0], box, compact ? "text-sm" : "text-base")}
        </div>
      );
    }

    if (memberCount === 2) {
      return (
        <div className={`${box} flex-shrink-0 relative`}>
          <div className="absolute top-0 left-0">
            {renderAvatar(otherMembers[0], half, "text-xs")}
          </div>
          <div className="absolute bottom-0 right-0">
            {renderAvatar(otherMembers[1], half, "text-xs")}
          </div>
        </div>
      );
    }

    if (memberCount === 3) {
      return (
        <div className={`${box} flex-shrink-0 relative`}>
          <div className="absolute top-0 left-1/2 -translate-x-1/2">
            {renderAvatar(otherMembers[0], third, "text-[10px]")}
          </div>
          <div className="absolute bottom-0 left-0">
            {renderAvatar(otherMembers[1], third, "text-[10px]")}
          </div>
          <div className="absolute bottom-0 right-0">
            {renderAvatar(otherMembers[2], third, "text-[10px]")}
          </div>
        </div>
      );
    }

    return (
      <div className={`${box} flex-shrink-0 grid grid-cols-2 gap-0.5`}>
        {otherMembers.slice(0, 4).map((member, idx) => (
          <div key={member.user_id || idx}>
            {renderAvatar(member, quarter, "text-[9px]")}
          </div>
        ))}
      </div>
    );
  },
);
RoomProfileAvatar.displayName = "RoomProfileAvatar";

export default RoomProfileAvatar;
